import React from 'react';
import styled from 'styled-components';
import {useState, useEffect} from 'react'
import data from '../data/simulatorDataset.json';

const SimulatorStyle = styled.div`
  width: 100%;
  max-width: 100%;
`;

const Card = styled.div`
  width: 100%;
  max-width: 720px;
  margin: 0;
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
  padding: 18px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
`;

const Label = styled.label`
  font-size: 0.9rem;
  color: var(--muted);
  font-weight: 650;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 12px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background-color: ${props => props.disabled ? 'rgba(12, 17, 20, 0.04)' : 'rgba(255, 255, 255, 0.8)'};
  color: ${props => props.disabled ? 'rgba(12, 17, 20, 0.45)' : 'var(--text)'};
  font-size: 1rem;
  cursor: pointer;

  &:focus-visible {
    outline: 3px solid rgba(38, 179, 168, 0.45);
    outline-offset: 3px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 6px;
`;

const Button = styled.button`
  padding: 12px 16px;
  cursor: pointer;
  background-color: var(--primary);
  border: none;
  border-radius: 999px;
  color: white;
  font-size: 1rem;
  font-weight: 750;

  &:hover { background: var(--primaryHover); }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:focus-visible {
    outline: 3px solid rgba(38, 179, 168, 0.45);
    outline-offset: 3px;
  }
`;

const SecondaryButton = styled.button`
  padding: 12px 16px;
  cursor: pointer;
  background-color: rgba(255, 255, 255, 0.65);
  border: 1px solid var(--border);
  border-radius: 999px;
  color: var(--text);
  font-size: 1rem;
  font-weight: 750;

  &:hover { background: rgba(255,255,255,0.9); }

  &:focus-visible {
    outline: 3px solid rgba(38, 179, 168, 0.45);
    outline-offset: 3px;
  }
`;

const Result = styled.div`
  border-radius: 14px;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.65);
  padding: 14px;
  line-height: 1.6;

  p {
    margin: 0;
  }
`;

const ResultHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const ResultBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 850;
  letter-spacing: -0.01em;
  border: 1px solid ${p => (p.$tone === 'bad' ? 'rgba(199, 63, 96, 0.25)' : 'rgba(38, 179, 168, 0.30)')};
  background: ${p => (p.$tone === 'bad' ? 'rgba(199, 63, 96, 0.10)' : 'rgba(38, 179, 168, 0.14)')};
  color: ${p => (p.$tone === 'bad' ? 'rgba(120, 24, 47, 0.95)' : 'rgba(15, 45, 44, 0.95)')};
`;

const ResultText = styled.div`
  border-radius: 14px;
  border: 1px solid rgba(12, 17, 20, 0.08);
  background: rgba(255, 255, 255, 0.82);
  padding: 12px 12px;

  strong {
    font-weight: 900;
  }
`;

function Simulator() {
    const [showResults, setShowResults] = useState(false);
    const [selectedDistrict, setSelectedDistrict] = useState('');
    const [parties, setParties] = useState([]);
    const [selectedParty, setSelectedParty] = useState('');
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (selectedDistrict) {
            setParties(Object.keys(data[selectedDistrict] || {}));
        }
    }, [selectedDistrict]);

    const handleDistrictChange = (e) => {
        setSelectedDistrict(e.target.value);
        setSelectedParty(''); // Reset party selection when district changes
    };

    const handlePartyChange = (e) => {
        setSelectedParty(e.target.value);
    };

    const handleReset = () => {
        setShowResults(false);
        setSelectedDistrict('');
        setSelectedParty('');
        setParties([]);
        setResult(null);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const partyValue = data[selectedDistrict][selectedParty];
        const seats = Number(partyValue);
        const wasted = seats <= 0;

        setResult({
            wasted,
            seats,
            district: selectedDistrict,
            party: selectedParty,
        });

        setShowResults(true);
    };

    return (
        <SimulatorStyle>
          <Card>
            {!showResults ? (
                <Form onSubmit={handleSubmit}>
                    <div>
                      <Label htmlFor="district">Círculo eleitoral</Label>
                      <Select name="district" id="district" value={selectedDistrict} onChange={handleDistrictChange}>
                          <option value="">Escolhe o círculo eleitoral</option>
                          {Object.keys(data).map((district) => (
                              <option key={district} value={district}>{district}</option>
                          ))}
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="party">Partido</Label>
                      <Select name="party" id="party" value={selectedParty} onChange={handlePartyChange} disabled={!selectedDistrict}>
                          <option value="">Escolhe o partido</option>
                          {parties.map((party) => (
                              <option key={party} value={party}>{party}</option>
                          ))}
                      </Select>
                    </div>

                    <ButtonRow>
                      <Button type="submit" disabled={!selectedDistrict || !selectedParty}>Ver resultado</Button>
                      <SecondaryButton type="button" onClick={handleReset}>Limpar</SecondaryButton>
                    </ButtonRow>
                </Form>
            ) : (
                <Form>
                    <Result aria-live="polite">
                      <ResultHeader>
                        <ResultBadge $tone={result?.wasted ? 'bad' : 'good'}>
                          {result?.wasted ? 'VOTO DESPERDIÇADO' : 'CONTA PARA A REPRESENTAÇÃO'}
                        </ResultBadge>
                        <span style={{ color: 'var(--muted)', fontWeight: 650 }}>
                          {result?.district} · {result?.party}
                        </span>
                      </ResultHeader>

                      <ResultText>
                        {result?.wasted ? (
                          <p>
                            Neste círculo, <strong>{result?.party}</strong> não elegeu deputados.
                            <br />
                            Resultado: este voto <strong>não alterou</strong> a distribuição de mandatos.
                          </p>
                        ) : (
                          <p>
                            Neste círculo, <strong>{result?.party}</strong> elegeu <strong>{result?.seats}</strong> deputado(s).
                            <br />
                            Resultado: este voto <strong>contribuiu</strong> para a representação.
                          </p>
                        )}
                      </ResultText>
                    </Result>

                    <p style={{ margin: 0, maxWidth: '60ch', lineHeight: 1.6, color: 'var(--muted)' }}>
                        Dica: experimenta comparar com outros partidos no mesmo círculo — ou com o mesmo partido noutros círculos.
                    </p>
                    <ButtonRow>
                      <Button type="button" onClick={handleReset}>Voltar a tentar</Button>
                    </ButtonRow>
                </Form>
            )}
          </Card>
        </SimulatorStyle>
    );
}

export default Simulator;